/**
 * tool/representation.jsx — D3 representation registry.
 *
 * The shipped representation keeps the D3 registry seam: tone and display
 * remain explicit fields even though this build uses one objective text variant.
 * Today's prototype ships exactly one combination: Objective × text.
 * Future variants can plug in without touching call sites. Every user-facing
 * string that varies by representation flows through useCopy(key).
 *
 * Usage in components:
 *
 *   import { useCopy } from './representation.jsx';
 *   ...
 *   const disclaimer = useCopy('mirror_disclaimer');
 *
 * Test seam: wrap the tree in <RepresentationProvider value={alt}> to
 * swap representations without prop-drilling. Production omits the
 * provider; useCopy falls back to REPRESENTATION_OBJECTIVE via the
 * context's default value.
 *
 * Missing keys: useCopy returns undefined (not throw — render-path
 * crashes are worse than a missing string for a research instrument).
 * In dev/test, useCopy logs a console.error so typos and unwired keys
 * are loud during development; in production the warning is silent so
 * a participant session can't be derailed by a copy-key regression.
 *
 * Scope of this module: registry + provider + useCopy + two routed sites
 * (Mirror disclaimer and F2 Hard-Stop title). Keep other copy strings local
 * until a second representation requires them.
 */

import { createContext, useContext } from 'react';

const isDev = (() => {
  try {
    return Boolean(import.meta.env?.DEV) || Boolean(import.meta.env?.MODE === 'test');
  } catch {
    return false;
  }
})();

/**
 * @typedef {Object} Representation
 * @property {'Objective' | 'Supportive' | 'Authoritative'} tone
 * @property {'text' | 'visual' | 'interactive'} display
 * @property {Record<string, string>} copy
 */

/** @type {Representation} */
export const REPRESENTATION_OBJECTIVE = Object.freeze({
  tone: 'Objective',
  display: 'text',
  copy: Object.freeze({
    // F1 Mirror — closing disclaimer at the bottom of the privacy receipt.
    mirror_disclaimer:
      'These are inferences a reader could make — not facts about you. ' +
      'Inferences fade as the conversation ages. Tap any snippet to revisit the source message.',

    // F2 Hard-Stop sheet — the headline above the inference list.
    f2_hardstop_title: 'Take a moment with this message.',
  }),
});

const RepresentationContext = createContext(REPRESENTATION_OBJECTIVE);

export function RepresentationProvider({ value, children }) {
  return <RepresentationContext.Provider value={value}>{children}</RepresentationContext.Provider>;
}

/**
 * Look up a copy string by key from a specific representation.
 * Use this in non-React contexts; React components should prefer
 * useCopy(key) so future representation changes propagate via context.
 *
 * @param {string} key
 * @param {Representation} [rep] defaults to the Objective baseline
 * @returns {string | undefined}
 */
export function getCopy(key, rep = REPRESENTATION_OBJECTIVE) {
  return rep.copy[key];
}

/**
 * React-side: read a copy string by key from the active representation.
 *
 * @param {string} key
 * @returns {string | undefined}
 */
export function useCopy(key) {
  const rep = useContext(RepresentationContext);
  const value = getCopy(key, rep);
  if (isDev && value === undefined) {
    // Loud in dev/test, silent in prod: a typo'd key here would render
    // as nothing on a participant screen. We want this caught at
    // development time, not after the user study.

    console.error(
      `[useCopy] missing key "${key}" on representation ${rep?.tone ?? 'unknown'}/${rep?.display ?? 'unknown'}`
    );
  }
  return value;
}
